import { TextField, TextFieldProps } from '@mui/material'
import { path } from 'ramda'
import React from 'react'
import { FieldError, FieldValues, Path, useFormContext } from 'react-hook-form'

import { toPathArray } from '@/app/component/form/utils/path'

type Props<T extends FieldValues> = {
  ariaLabel?: string
  endAdornment?: React.ReactNode
  inputMode?: React.InputHTMLAttributes<HTMLInputElement>['inputMode']
  name: Path<T>
  info?: React.ReactNode
  startAdornment?: React.ReactNode
} & Pick<
  TextFieldProps,
  | 'label'
  | 'type'
  | 'required'
  | 'disabled'
  | 'placeholder'
  | 'multiline'
  | 'rows'
  | 'autoComplete'
  | 'autoFocus'
  | 'size'
  | 'sx'
>

export function RHFTextField<T extends FieldValues>({
  ariaLabel,
  autoComplete = 'off',
  endAdornment,
  info,
  inputMode,
  name,
  startAdornment,
  ...props
}: Props<T>) {
  const inputRef = React.useRef<HTMLInputElement | HTMLTextAreaElement>()

  const { register, formState } = useFormContext<T>()
  const error = path<FieldError>(toPathArray(name), formState.errors)

  const preventNumberInputChange = (e: Event) => {
    if (props.type === 'number') {
      e.preventDefault()
    }
  }

  React.useEffect(() => {
    if (!props.autoFocus) {
      return undefined
    }

    const timeout = setTimeout(() => {
      inputRef.current?.focus({
        preventScroll: true,
      })
    }, 0) // to push it to the end of the event loop

    return () => {
      clearTimeout(timeout)
    }
  }, [props.autoFocus])

  return (
    <TextField
      error={!!error}
      fullWidth
      helperText={error?.message || info}
      inputRef={inputRef}
      size="small"
      slotProps={{
        htmlInput: {
          autoComplete,
          autoFocus: props.autoFocus,
          inputMode,
          ...register(name, {
            valueAsNumber: props.type === 'number',
            setValueAs: (value) => value || undefined,
          }),
          'aria-label': ariaLabel || name,
        },
        input: {
          startAdornment,
          endAdornment,
        },
        inputLabel: { shrink: true },
      }}
      sx={{ mb: 2 }}
      variant="standard"
      onBlur={(e) => e.target.removeEventListener('wheel', preventNumberInputChange)}
      onFocus={(e) => e.target.addEventListener('wheel', preventNumberInputChange, { passive: false })}
      onKeyDown={(e) => {
        if (props.type === 'number' && ['ArrowUp', 'ArrowDown'].includes(e.key)) {
          e.preventDefault()
        }
      }}
      {...props}
    />
  )
}
